import { useEffect, useState } from "react";
import { Activity, GitBranch, Eye, ShieldCheck, Layers, AlertTriangle, FileSearch } from "lucide-react";
import { Link } from "react-router-dom";
import ConsoleLayout from "@/components/equilibrium/ConsoleLayout";
import { fetchEqOverview, fetchEqCoherence, fetchEqProvenance } from "@/lib/eq";

const ICONS = {
  spyglass: Eye,
  atlasbridge: GitBranch,
  horizon: Activity,
  dutydeck: ShieldCheck,
  oli: Layers,
};

export default function ConsoleOverview() {
  const [overview, setOverview] = useState(null);
  const [coh, setCoh] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEqOverview().then(setOverview).catch(() => {});
    fetchEqCoherence().then(setCoh).catch(() => {});
    fetchEqProvenance(8).then(setEvents).catch(() => {});
  }, []);

  return (
    <ConsoleLayout>
      <div data-testid="eq-console-overview">
        <div className="num-label">OPERATOR · CONSOLE</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-axiom-text font-bold tracking-tight">
          Operational equilibrium snapshot.
        </h1>
        <p className="mt-2 text-axiom-muted text-sm max-w-2xl">
          Coherence, entropy, intervention pressure, and provenance velocity across the full
          Equilibrium-AI module set.
        </p>

        {/* KPI row */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            { k: "SYSTEM COHERENCE", v: overview ? `${overview.system_coherence_pct}%` : "—", accent: true, testid: "eq-stat-coherence" },
            { k: "PROVENANCE · 24H", v: overview?.provenance_24h ?? "—", testid: "eq-stat-prov-24h" },
            { k: "INTERVENTIONS · 24H", v: overview?.interventions_24h ?? "—", testid: "eq-stat-interventions" },
            { k: "DIAGNOSTICS · TOTAL", v: overview?.diagnostics_total ?? "—", testid: "eq-stat-diagnostics" },
          ].map((m) => (
            <div key={m.k} className="bg-axiom-bg p-5" data-testid={m.testid}>
              <div className="num-label">{m.k}</div>
              <div
                className={`mt-3 font-display text-3xl font-bold ${
                  m.accent ? "text-axiom-accent" : "text-axiom-text"
                }`}
              >
                {m.v}
              </div>
            </div>
          ))}
        </div>

        {/* Coherence chart + module column */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-white/10 bg-axiom-bg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="num-label">COHERENCE · 12 HR</div>
                <div className="font-display text-lg text-axiom-text mt-1">
                  Signal coherence vs. entropy delta
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs font-mono text-axiom-muted">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-px bg-axiom-accent" /> coherence
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-px bg-amber-300" /> entropy
                </span>
              </div>
            </div>
            <CoherenceChart data={coh?.series || []} />
          </div>

          <div className="border border-white/10 bg-axiom-bg p-6">
            <div className="num-label mb-4">MODULES</div>
            <div className="space-y-3">
              {(coh?.modules || []).map((m) => {
                const Icon = ICONS[m.key] || Activity;
                return (
                  <Link
                    key={m.key}
                    to={`/equilibrium/modules/${m.key}`}
                    className="flex items-center gap-3 border border-white/10 hover:border-axiom-accent/40 p-3 transition-colors"
                  >
                    <Icon size={14} className="text-axiom-accent" strokeWidth={1.6} />
                    <span className="font-mono text-xs uppercase text-axiom-text">
                      {m.key}
                    </span>
                    <span className="flex-1" />
                    <span className="text-xs font-mono text-axiom-muted">
                      {m.coherence}%
                    </span>
                    <span
                      className={`text-[10px] font-mono px-2 py-0.5 border ${
                        m.status === "OPERATIONAL"
                          ? "border-emerald-400/40 text-emerald-300"
                          : m.status === "PROVISIONAL"
                          ? "border-amber-400/40 text-amber-300"
                          : "border-axiom-accent/40 text-axiom-accent"
                      }`}
                    >
                      {m.status}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent provenance */}
        <div className="mt-8 border border-white/10 bg-axiom-bg">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <FileSearch size={14} className="text-axiom-accent" />
              <span className="num-label">RECENT PROVENANCE</span>
            </div>
            <Link
              to="/equilibrium/console/provenance"
              className="text-xs text-axiom-muted hover:text-axiom-text"
            >
              View full ledger →
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {events.map((e) => (
              <ProvenanceRow key={e.id} e={e} />
            ))}
            {events.length === 0 && (
              <div className="p-6 text-sm text-axiom-muted">No events yet.</div>
            )}
          </div>
        </div>
      </div>
    </ConsoleLayout>
  );
}

function CoherenceChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-xs font-mono text-axiom-dim">no series</div>;
  }
  const w = 800;
  const h = 200;
  const padding = 16;
  const xs = data.map((_, i) => padding + (i * (w - padding * 2)) / (data.length - 1));
  const cohY = data.map((d) => padding + (h - padding * 2) * (1 - (d.coherence - 78) / (99.5 - 78)));
  const entY = data.map((d) => padding + (h - padding * 2) * (1 - d.entropy / 0.55));

  const cohPath = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${cohY[i].toFixed(1)}`).join(" ");
  const entPath = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${entY[i].toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none">
      {/* Grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <line
          key={t}
          x1={padding}
          x2={w - padding}
          y1={padding + (h - padding * 2) * t}
          y2={padding + (h - padding * 2) * t}
          stroke="rgba(255,255,255,0.05)"
        />
      ))}
      <path d={cohPath} stroke="#3B82F6" strokeWidth="1.5" fill="none" />
      <path d={entPath} stroke="#fbbf24" strokeWidth="1.25" fill="none" strokeDasharray="3 3" opacity="0.75" />
      <circle cx={xs[xs.length - 1]} cy={cohY[cohY.length - 1]} r="3" fill="#3B82F6" />
    </svg>
  );
}

function ProvenanceRow({ e }) {
  const sevColor =
    e.severity === "intervene"
      ? "text-amber-300"
      : e.severity === "watch"
      ? "text-axiom-accent"
      : "text-axiom-muted";
  return (
    <div className="grid grid-cols-12 items-center px-4 py-3 text-xs">
      <div className="col-span-3 font-mono text-axiom-dim">
        {new Date(e.timestamp).toLocaleString(undefined, {
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </div>
      <div className="col-span-2 font-mono uppercase text-axiom-text">{e.source_module}</div>
      <div className="col-span-2 font-mono text-axiom-muted truncate">{e.signal_type}</div>
      <div className="col-span-1 font-mono">
        <span className={sevColor}>Δ{e.entropy_delta}</span>
      </div>
      <div className={`col-span-1 font-mono uppercase ${sevColor}`}>{e.severity}</div>
      <div className="col-span-3 text-axiom-muted truncate">{e.description}</div>
    </div>
  );
}
